import React, { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LoaderWrapper } from "~/components/LoaderWrapper";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import { UploadProvider } from "~/components/UploadProvider/UploadProvider";
import MagicLinkLoginPage from "~/pages/Onboarding/MagicLinkLoginPage";
import { OnboardingDialog } from "~/pages/Onboarding/OnboardingDialog";
import SetPasswordPage from "~/pages/Onboarding/SetPasswordPage";
import PublicInvoicePage from "~/pages/PublicInvoices/PublicInvoicePage";
import PublicPaymentCompletedPage from "~/pages/PublicInvoices/PublicPaymentCompleted";
import PublicPaymentCreditCard3dsProcessing from "~/pages/PublicInvoices/PublicPaymentCreditCard3dsProcessing";
import PublicPaymentCreditCardCompleted from "~/pages/PublicInvoices/PublicPaymentCreditCardCompleted";
import PublicPaymentPage from "~/pages/PublicInvoices/PublicPaymentPage";
import AuthenticationProcessingPage from "~/pages/Subscription/AuthenticationProcessingPage";
import { ForbiddenAccessForAppSumo } from "~/routes/ForbiddenAccessForAppSumo";
import { NotRetired } from "~/routes/NotRetired";
import { NotSuspended } from "~/routes/NotSuspended";
import { RequireActive } from "~/routes/RequireActive";
import { RequireChatAvailable } from "~/routes/RequireChatAvailable";
import { RequireNotSuspended } from "~/routes/RequireNotSuspended";
import { RequireOnDemand } from "~/routes/RequireOnDemand";
import Angie from "~/utils/angie";
import { AppResolver } from "./AppResolver";
import MainRoutes from "./MainRoutes";
import { NotAuthenticated } from "./NotAuthenticated";
import { Providers } from "./Providers";
import { RequireAccess } from "./RequireAccess";
import { APP_SUMO_PLANS, PER_SEAT_PLAN, RequireActivatedAndAllowedPlan } from "./RequireActivatedAndAllowedPlan";
import { RequireAuth } from "./RequireAuth";

const LoginPage = lazy(() => import("~/pages/LoginPage"));
const PasswordRecoveryPage = lazy(() => import("~/pages/PasswordRecoveryPage"));
const ResetPasswordPage = lazy(() => import("~/pages/ResetPasswordPage"));
const PublicUnsubscribePage = lazy(() => import("~/pages/PublicUnsubscribePage"));
const ChatPage = lazy(() => import("~/pages/Chat"));
const Layout = lazy(() => import("~/pages/Layout"));
const ManageAddOnsPage = lazy(() => import("~/pages/Subscription/GetPaidBundlePage"));
const SubscriptionPage = lazy(() => import("~/pages/Subscription/SubscriptionPage"));
const ChoosePlanPage = lazy(() => import("~/pages/Subscription/ChoosePlanPage"));

const ManageStorageAddOnsPage = lazy(() => import("~/pages/Subscription/ManageStorageAddonsPage"));

const ManageExtraUsersAddOnsPage = lazy(() => import("~/pages/Subscription/ManageExtraUsersAddOnsPage"));

const RetiredPage = lazy(() => import("~/pages/Retirement/RetiredPage"));

const SuspendedPage = lazy(() => import("~/pages/Suspension/SuspendedPage"));

const AcceptInvitationPage = lazy(() => import("~/pages/People/Invitation/AcceptInvitationPage"));

export const AppRoutes = () => {
  return (
    <BrowserRouter basename={Angie.basename.length > 0 ? Angie.basename : undefined}>
      <Routes>
        <Route
          exact
          path="/login"
          element={
            <NotAuthenticated>
              <Suspense fallback={<LoaderWrapper />}>
                <LoginPage />
              </Suspense>
            </NotAuthenticated>
          }
        />
        <Route
          exact
          path="/password-recovery/forgot-password"
          element={
            <NotAuthenticated>
              <Suspense fallback={<LoaderWrapper />}>
                <PasswordRecoveryPage />
              </Suspense>
            </NotAuthenticated>
          }
        />
        <Route
          exact
          path="/password-recovery/reset-password"
          element={
            <NotAuthenticated>
              <Suspense fallback={<LoaderWrapper />}>
                <ResetPasswordPage />
              </Suspense>
            </NotAuthenticated>
          }
        />
        <Route
          exact
          path="/public/notifications/unsubscribe"
          element={
            <NotAuthenticated>
              <Suspense fallback={<LoaderWrapper />}>
                <PublicUnsubscribePage />
              </Suspense>
            </NotAuthenticated>
          }
        />
        <Route
          exact
          path="/magic-link-login"
          element={
            <Suspense fallback={<LoaderWrapper />}>
              <MagicLinkLoginPage />
            </Suspense>
          }
        />
        <Route
          exact
          path="/accept-invitation"
          element={
            <NotAuthenticated>
              <UploadProvider>
                <Suspense fallback={<LoaderWrapper />}>
                  <AcceptInvitationPage />
                </Suspense>
              </UploadProvider>
            </NotAuthenticated>
          }
        />
        <Route
          exact
          path="/retired"
          element={
            <NotRetired>
              <Suspense fallback={<LoaderWrapper />}>
                <RetiredPage />
              </Suspense>
            </NotRetired>
          }
        />
        <Route
          exact
          path="/suspended"
          element={
            <NotSuspended>
              <Suspense fallback={<LoaderWrapper />}>
                <SuspendedPage />
              </Suspense>
            </NotSuspended>
          }
        />
        <Route path="s">
          <Route
            exact
            path="invoice"
            element={
              <Suspense fallback={<LoaderWrapper />}>
                <PublicInvoicePage />
              </Suspense>
            }
          />
          <Route
            exact
            path="payment"
            element={
              <Suspense fallback={<LoaderWrapper />}>
                <PublicPaymentPage />
              </Suspense>
            }
          />
          <Route
            exact
            path="payment-completed"
            element={
              <Suspense fallback={<LoaderWrapper />}>
                <PublicPaymentCompletedPage />
              </Suspense>
            }
          />
          <Route
            exact
            path="payment-completed-card"
            element={
              <Suspense fallback={<LoaderWrapper />}>
                <PublicPaymentCreditCardCompleted />
              </Suspense>
            }
          />
          <Route
            exact
            path="payment-processing"
            element={
              <Suspense fallback={<LoaderWrapper />}>
                <PublicPaymentCreditCard3dsProcessing />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/*"
          element={
            <AppResolver>
              <RequireActive>
                <RequireAuth>
                  <Providers>
                    <Routes>
                      <Route
                        path="onboarding"
                        element={
                          <RequireOnDemand>
                            <Suspense fallback={<OnboardingDialog />}>
                              <SetPasswordPage />
                            </Suspense>
                          </RequireOnDemand>
                        }
                      />
                      <Route
                        exact
                        path="chat"
                        element={
                          <RequireAccess section="chat">
                            <RequireNotSuspended>
                              <RequireChatAvailable>
                                <Suspense fallback={<LoaderWrapper />}>
                                  <ChatPage />
                                </Suspense>
                              </RequireChatAvailable>
                            </RequireNotSuspended>
                          </RequireAccess>
                        }
                      />
                      <Route path="subscription">
                        <Route
                          index
                          element={
                            <RequireAccess section="subscription">
                              <Suspense fallback={<LoaderWrapper />}>
                                <SubscriptionPage />
                              </Suspense>
                            </RequireAccess>
                          }
                        />
                        <Route
                          path="choose-plan"
                          element={
                            <RequireAccess section="subscription">
                              <ForbiddenAccessForAppSumo>
                                <Suspense fallback={<LoaderWrapper />}>
                                  <ChoosePlanPage />
                                </Suspense>
                              </ForbiddenAccessForAppSumo>
                            </RequireAccess>
                          }
                        />
                        <Route
                          path="manage-add-ons"
                          element={
                            <RequireAccess section="subscription">
                              <RequireActivatedAndAllowedPlan allowedPlans={[...PER_SEAT_PLAN, ...APP_SUMO_PLANS]}>
                                <Suspense fallback={<LoaderWrapper />}>
                                  <ManageAddOnsPage />
                                </Suspense>
                              </RequireActivatedAndAllowedPlan>
                            </RequireAccess>
                          }
                        />
                        <Route
                          path="manage-add-ons/storage"
                          element={
                            <RequireAccess section="subscription">
                              <RequireActivatedAndAllowedPlan allowedPlans={PER_SEAT_PLAN}>
                                <Suspense fallback={<LoaderWrapper />}>
                                  <ManageStorageAddOnsPage />
                                </Suspense>
                              </RequireActivatedAndAllowedPlan>
                            </RequireAccess>
                          }
                        />
                        <Route
                          path="authentication-process"
                          element={
                            <RequireAccess section="subscription">
                              <Suspense fallback={<LoaderWrapper />}>
                                <AuthenticationProcessingPage />
                              </Suspense>
                            </RequireAccess>
                          }
                        />
                        <Route
                          path="manage-add-ons/extra-members"
                          element={
                            <RequireAccess section="subscription">
                              <RequireActivatedAndAllowedPlan allowedPlans={APP_SUMO_PLANS}>
                                <Suspense fallback={<LoaderWrapper />}>
                                  <ManageExtraUsersAddOnsPage />
                                </Suspense>
                              </RequireActivatedAndAllowedPlan>
                            </RequireAccess>
                          }
                        />
                      </Route>
                      <Route
                        path="*"
                        element={
                          <RequireNotSuspended>
                            <Suspense fallback={<SectionLoader className="tw-h-screen tw-min-h-screen" />}>
                              <Layout />
                            </Suspense>
                          </RequireNotSuspended>
                        }
                      >
                        <Route
                          path="*"
                          element={<MainRoutes />}
                        />
                      </Route>
                    </Routes>
                  </Providers>
                </RequireAuth>
              </RequireActive>
            </AppResolver>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
